/**
 * date-value-accessor.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Directive, ElementRef, forwardRef, Renderer } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TypedData } from "../../common/data/typed-data";

/**
 * This constant is a provider that ultimately points the directive class at
 * itself for the purposes of providing a value accessor.
 */
export const DATE_VALUE_ACCESSOR : any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DateValueAccessor),
  multi: true
};

/**
 * The {@link DateValueAccessor} is a directive that overrides date inputs to
 * behave more sanely relative to their input values. Specifically it binds
 * the semantic meaning of the date to a javascript Date object (rather than
 * a raw string representation).
 */
@Directive({
  selector: `
    input[type=date][formControl]
`,
  host: {
    "(change)": "filterNative($event)",
    "(blur)": "reissueAsChange($event)",
    "(keyup.enter)": "reissueAsChange($event)"
  },
  providers: [DATE_VALUE_ACCESSOR]
})
export class DateValueAccessor implements ControlValueAccessor
{
  /**
   * The change event occurs too often on [type=date] inputs to represent the
   * semantics we'd like (it will emit after most user events, so the vast
   * majority of the values it sends will be invalid dates or partial changes).
   * Instead we reinterpret blur/enter as change and ditch change events.
   */

  /**
   * Take a blur or keyup.enter event and issue a corresponding change event,
   * with the special flag "reissued" to distinguish from native change events.
   * @param existing A blur or keyup Event on the host
   */
  private reissueAsChange (existing : Event) : void
  {
    if (existing.type === "blur")
    {
      this.onTouched();
    }
    const event : Event & {reissued? : boolean} =
      new Event("change", {bubbles: true});
    event.reissued = true;
    (this.elementRef.nativeElement as HTMLElement).dispatchEvent(event);
  }

  /**
   * Stop the propagation of native change events and handle the reissued change
   * events from the above method by passing along to internalChange.
   * @param event A change Event on the host
   */
  private filterNative (event : Event & {reissued? : boolean}) : void
  {
    if (!event.reissued)
    {
      event.stopPropagation();
    }
    else
    {
      this.internalChange((event.target as HTMLInputElement).value);
    }
  }

  /**
   * The function to invoke when the controlled radio input element's value
   * changes.
   */
  public onChange = (_ : any) => {
    // By default nothing, but will be set by formControl
  }

  /**
   * The function to invoke when the controlled radio input element becomes
   * touched.
   */
  public onTouched = () => {
    // By default nothing, but will be set by formControl
  }

  /**
   * Convert the raw date value to the associated Date object.
   *
   * @param value The raw input's value
   */
  public internalChange (value : any) : void
  {
    if (typeof value !== "string")
    {
      console.error("Date input value error", value);
      throw new Error("Date input element returned non-string value");
    }
    const date : Date = TypedData.inputToDate(value);

    if (DateValueAccessor.USE_2_DIGIT_FIX
        && date !== null
        && !isNaN(date.valueOf()))
    {
      const yr : number = date.getUTCFullYear();
      if (yr < 100 && yr > 0)
      {
        date.setUTCFullYear(this.interpret2DigitYear(yr));
      }
    }

    this.onChange(date);
  }

  private static USE_2_DIGIT_FIX : boolean = true;
  private static CENTURY_TOLERANCE : number = 1;

  private interpret2DigitYear (year : number) : number
  {
    const cy : number = (new Date()).getUTCFullYear();
    const current2Digit : number = cy % 100;
    const currentCentury : number = Math.floor(cy / 100) * 100;
    if (year < current2Digit + DateValueAccessor.CENTURY_TOLERANCE)
    {
      return currentCentury + year;
    }
    else
    {
      return currentCentury - 100 + year;
    }
  }

  /**
   * Construct a {@link RadioControlValueAccessor}.
   *
   * @param renderer
   *        A {@link Renderer}.
   * @param elementRef
   *        The {@link ElementRef} of the base element.
   */
  constructor (
    private renderer : Renderer,
    private elementRef : ElementRef
  )
  {
    /* Nothing. */
  }

  /**
   * A helper to set the raw value of the input element.
   *
   * @param value The value to set
   */
  private setInputValue (value : string) : void
  {
    this.renderer.setElementProperty(
      this.elementRef.nativeElement,
      "value",
      value
    );
  }

  /**
   * Write a new value to the <input> element.
   *
   * @param value
   *        The replacement value.
   */
  public writeValue (value : any) : void
  {
    if (value === null || value === undefined)
    {
      return this.setInputValue("");
    }

    if (!(value instanceof Date))
    {
      // tslint:disable-next-line:no-debugger
      debugger;
      console.error("Model is not a Date:", value);
      throw new Error("Only Date objects can be bound to a date-control!");
    }

    this.setInputValue(TypedData.dateToInput(value));
  }

  /**
   * Set the function to invoke when the controlled radio input element's value
   * changes.
   *
   * @param fn
   *        The replacement function.
   */
  public registerOnChange (fn : (_ : any) => {}) : void
  {
    this.onChange = fn;
  }

  /**
   * Set the function to invoke when the controlled radio input element becomes
   * touched.
   *
   * @param fn
   *        The replacement function.
   */
  public registerOnTouched (fn : () => {}) : void
  {
    this.onTouched = fn;
  }

  /**
   * Set whether the control is enabled or disabled.
   */
  public setDisabledState (isDisabled : boolean) : void {
    this.renderer.setElementProperty(
      this.elementRef.nativeElement,
      "disabled",
      isDisabled
    );
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/APIs/date-value-accessor.ts